import { useState, useEffect, useCallback, type ReactNode } from "react";
import { PickupContext, type PickupContextType } from "./PickupContext";
import {
  callCreatePickup,
  callDeletePickup,
  callFetchPickups,
  callUpdatePickup,
  CreatePickupPayload,
  type Pickup,
  type UpdatePickupPayload,
} from "../../../api/pickups";
import { useAuthUser } from "../hooks/useAuthUser";

interface PickupProviderProps {
  children: ReactNode;
}

export const PickupContextProvider = ({ children }: PickupProviderProps) => {
  const { isAuthenticated } = useAuthUser();
  const [pickups, setPickups] = useState<Pickup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPickups = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await callFetchPickups();
      setPickups(data);
    } catch (err) {
      console.error("Error fetching pickups:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch pickups");
      setPickups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const createPickup = async (data: CreatePickupPayload): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const created = await callCreatePickup(data);
      setPickups((prev) => [...prev, created]);
    } catch (err) {
      console.error("Error adding pickup:", err);
      setError(err instanceof Error ? err.message : "Failed to add pickup");
    } finally {
      setLoading(false);
    }
  };

  const updatePickup = async (
    id: string,
    data: UpdatePickupPayload,
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const updated = await callUpdatePickup(id, data);
      setPickups((prev) =>
        prev.map((pickup) => (pickup.id === id ? updated : pickup)),
      );
    } catch (err) {
      console.error("Error updating pickup:", err);
      setError(err instanceof Error ? err.message : "Failed to update pickup");
    } finally {
      setLoading(false);
    }
  };

  const deletePickup = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await callDeletePickup(id);
      setPickups((prev) => prev.filter((pickup) => pickup.id !== id));
    } catch (err) {
      console.error("Error deleting pickup:", err);
      setError(err instanceof Error ? err.message : "Failed to delete pickup");
    } finally {
      setLoading(false);
    }
  };

  const refreshPickups = async (): Promise<void> => {
    await fetchPickups();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setPickups([]);
      setLoading(false);
      return;
    }
    void fetchPickups();
  }, [isAuthenticated, fetchPickups]);

  const value: PickupContextType = {
    pickups,
    loading,
    error,
    fetchPickups,
    createPickup,
    updatePickup,
    deletePickup,
    refreshPickups,
  };

  return (
    <PickupContext.Provider value={value}>{children}</PickupContext.Provider>
  );
};
